import { useEffect } from "react";

/** Production origin — canonical URLs and JSON-LD `url`s resolve against it. */
export const SITE_URL = "https://hireos.co.nz";

export interface SeoConfig {
  /** Document title. Omit to leave the current <title> untouched. */
  title?: string;
  /** Meta description for this route. */
  description: string;
  /** Canonical URL — an absolute URL or a site-relative path ("/inquiry"). */
  canonical: string;
	/** Tenant/business name used for social metadata. */
	siteName?: string;
  /** JSON-LD structured-data objects injected into <head> for this route. */
  jsonLd?: Array<Record<string, unknown>>;
}

/**
 * Per-route SEO for the SPA (#147).
 *
 * #138 set the site-wide signals statically in index.html. This hook
 * layers per-route refinements on top client-side: it updates <title>,
 * <meta name="description"> and <link rel="canonical">, and injects any
 * JSON-LD <script>s into <head>. On unmount it restores the previous
 * values, so navigating to a route that does NOT call useSeo falls back
 * cleanly to the index.html baseline.
 */
export function useSeo({ title, description, canonical, siteName, jsonLd }: SeoConfig): void {
  // Serialise jsonLd so a fresh array literal on every render does not
  // re-fire the effect — only a genuine content change does.
  const jsonLdKey = JSON.stringify(jsonLd ?? []);

  useEffect(() => {
    const head = document.head;

    // ── <title> ──
    const prevTitle = document.title;
    if (title) document.title = title;

    // ── <meta name="description"> ──
    let descEl = head.querySelector<HTMLMetaElement>('meta[name="description"]');
    const createdDesc = descEl === null;
    if (!descEl) {
      descEl = document.createElement("meta");
      descEl.setAttribute("name", "description");
      head.appendChild(descEl);
    }
    const prevDesc = descEl.getAttribute("content");
    descEl.setAttribute("content", description);

    // ── <link rel="canonical"> ──
    let linkEl = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const createdLink = linkEl === null;
    if (!linkEl) {
      linkEl = document.createElement("link");
      linkEl.setAttribute("rel", "canonical");
      head.appendChild(linkEl);
    }
    const prevHref = linkEl.getAttribute("href");
    linkEl.setAttribute(
      "href",
      canonical.startsWith("http") ? canonical : SITE_URL + canonical,
    );

	const socialFields: Array<[string, string | undefined, "name" | "property"]> = [
		["og:title", title, "property"],
		["og:description", description, "property"],
		["og:url", canonical.startsWith("http") ? canonical : SITE_URL + canonical, "property"],
		["og:site_name", siteName, "property"],
		["twitter:title", title, "name"],
		["twitter:description", description, "name"],
	];
	const socialPrevious = socialFields.map(([key, value, attr]) => {
		const selector = `meta[${attr}="${key}"]`;
		let el = head.querySelector<HTMLMetaElement>(selector);
		const created = el === null;
		if (!el) {
			el = document.createElement("meta");
			el.setAttribute(attr, key);
			head.appendChild(el);
		}
		const previous = el.getAttribute("content");
		if (value) el.setAttribute("content", value);
		else el.removeAttribute("content");
		return { el, created, previous };
	});

    // ── JSON-LD ──
    const parsed = JSON.parse(jsonLdKey) as Array<Record<string, unknown>>;
    const scripts = parsed.map((data) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.dataset.seoJsonld = "";
      el.textContent = JSON.stringify(data);
      head.appendChild(el);
      return el;
    });

    return () => {
      document.title = prevTitle;
      if (createdDesc) {
        descEl.remove();
      } else if (prevDesc !== null) {
        descEl.setAttribute("content", prevDesc);
      }
      if (createdLink) {
        linkEl.remove();
      } else if (prevHref !== null) {
        linkEl.setAttribute("href", prevHref);
      }
		for (const { el, created, previous } of socialPrevious) {
			if (created) el.remove();
			else if (previous !== null) el.setAttribute("content", previous);
			else el.removeAttribute("content");
		}
      for (const el of scripts) el.remove();
    };
  }, [title, description, canonical, siteName, jsonLdKey]);
}
